/*
  RK: gedeeld horizontaal-slepen-gedrag ("swipe-rij"), herbruikbaar voor elke
  rij producten/items in de winkel.

  Gebruik door een aanroepende sectie:

    <rk-swipe>
      <div class="rk-swipe__track">
        <div class="rk-swipe__item">...</div>
        ...
      </div>
    </rk-swipe>

  !! DIT STOND ALS INLINE SCRIPT IN snippets/rk-swipe.liquid !!
  Gemeten op de homepage van de live winkel: die snippet werd door zeven
  secties gerenderd, dus stond dit script - plus de bijbehorende opmaak -
  zeven keer in hetzelfde HTML-document. De guard `customElements.get(...)`
  zorgde er alleen voor dat er een keer een custom element werd GEDEFINIEERD,
  niet dat de tekst een keer werd VERSTUURD. Nu wordt dit bestand een keer
  opgehaald en gecachet. Zelfde patroon als assets/rk-card.js.
  Zet het daar dus nooit terug.

  DE TEKSTEN KOMEN VIA window.RK_SWIPE_I18N (snippets/rk-system.liquid), want
  een .js-bestand kan de taal-editor van Shopify niet zelf lezen. Staat die
  niet klaar, dan valt alles terug op lege tekst en gebeurt er verder niets
  raars.

  "VEEG VOOR MEER" - ALLEEN ONDER EEN PRODUCTRIJ
  Onder de voortgangslijn van een PRODUCTRIJ komt een klein regeltje dat zegt
  dat je kunt vegen. Nadrukkelijk alleen daar: de USP-strook, de
  Instagram-rij, de klantfoto's en elke andere schuifrij krijgen niets.
  Daarom kijkt _init() hieronder of de track de klasse .rk-product-row__track
  draagt - die zetten sections/rk-product-row, rk-featured-collection en
  rk-recommendations met opzet alle drie op hun rij, juist zodat ze als een
  soort te herkennen zijn. Draagt een rij die klasse niet, dan wordt het
  regeltje niet eens gebouwd. De opmaak staat in assets/rk-swipe-hint.css.

  STAPSGEWIJS SCHUIVEN (scroll-snap): elke rij eindigt na een veeg netjes op
  een heel item. Bewust GEEN scroll-snap-stop: always - dat dwingt een item
  per veeg af, prettig bij vier USP's maar taai bij dertig producten. De
  USP-strook zet die regel zelf wel aan.

  EEN KLIK = EEN ITEM: de stap wordt per klik opnieuw gemeten aan het eerste
  item (breedte + gap).

  TIMING VAN connectedCallback: dit bestand laadt met defer, dus
  customElements.define() draait pas als de pagina is uitgeparsed. Toch blijft
  de wachtlus in _init() staan: in de thema-editor wordt een sectie opnieuw
  ingevoegd terwijl de rij nog wordt opgebouwd, en dan vuurt
  connectedCallback alsnog voordat .rk-swipe__track bestaat.
*/
(() => {
  if (customElements.get('rk-swipe')) return;

  const ARROW_SVG_NEXT = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ARROW_SVG_PREV = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const I18N = window.RK_SWIPE_I18N || {};

  /* RK: de tekst onder de voortgangslijn. Uit de taal-editor, niet uit de
     code - zie "VEEG VOOR MEER" in de kop. Is de sleutel leeg gemaakt, dan
     wordt het regeltje nergens gebouwd. */
  const HINT_TEXT = I18N.hint || '';

  /* RK: en dit is de rem die hem tot de PRODUCTRIJEN beperkt. Alleen die
     rijen dragen deze klasse. Zie de kop. */
  const HINT_TRACK_CLASS = 'rk-product-row__track';

  class RkSwipe extends HTMLElement {
    constructor() {
      super();
      this._ready = false;
      this._track = null;
      this._bar = null;
      this._fill = null;
      this._hint = null;
      this._dragging = false;
      this._dragStartX = 0;
      this._dragStartScroll = 0;

      this._onTrackScroll = this._updateBar.bind(this);
      this._onBarPointerDown = this._onBarPointerDown.bind(this);
      this._onBarPointerMove = this._onBarPointerMove.bind(this);
      this._onBarPointerUp = this._onBarPointerUp.bind(this);
      this._onResize = this._onResize.bind(this);
    }

    connectedCallback() {
      this._init();
    }

    _init() {
      if (this._ready) return;

      const track = this.querySelector(':scope > .rk-swipe__track');
      if (!track) {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this._init(), { once: true });
        } else {
          requestAnimationFrame(() => {
            if (this.querySelector(':scope > .rk-swipe__track')) this._init();
          });
        }
        return;
      }

      this._ready = true;
      this._track = track;

      const viewport = document.createElement('div');
      viewport.className = 'rk-swipe__viewport';
      track.parentNode.insertBefore(viewport, track);
      viewport.appendChild(track);

      const prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'rk-swipe__arrow rk-swipe__arrow--prev';
      prev.setAttribute('aria-label', I18N.prev || 'Previous');
      prev.innerHTML = ARROW_SVG_PREV;
      prev.addEventListener('click', () => this._step(-1));
      viewport.appendChild(prev);

      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'rk-swipe__arrow rk-swipe__arrow--next';
      next.setAttribute('aria-label', I18N.next || 'Next');
      next.innerHTML = ARROW_SVG_NEXT;
      next.addEventListener('click', () => this._step(1));
      viewport.appendChild(next);

      const bar = document.createElement('div');
      bar.className = 'rk-swipe__bar';
      const fill = document.createElement('div');
      fill.className = 'rk-swipe__bar-fill';
      bar.appendChild(fill);
      this.appendChild(bar);
      this._bar = bar;
      this._fill = fill;

      /* RK: "veeg voor meer", direct onder de lijn en ALLEEN bij een
         productrij. Wanneer hij verder te zien is bepaalt
         assets/rk-swipe-hint.css plus _syncHint() hieronder. */
      if (HINT_TEXT && track.classList.contains(HINT_TRACK_CLASS)) {
        const hint = document.createElement('div');
        hint.className = 'rk-swipe__hint';
        hint.textContent = HINT_TEXT;
        this.appendChild(hint);
        this._hint = hint;
      }

      track.addEventListener('scroll', this._onTrackScroll, { passive: true });
      bar.addEventListener('pointerdown', this._onBarPointerDown);
      window.addEventListener('resize', this._onResize);
      window.addEventListener('orientationchange', this._onResize);
      window.addEventListener('load', this._onResize);

      if (window.ResizeObserver) {
        this._resizeObserver = new ResizeObserver(this._onResize);
        this._resizeObserver.observe(track);
      }

      if (window.IntersectionObserver) {
        this._io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) this._onResize();
        });
        this._io.observe(track);
      }

      this._onResize();
      [0, 300, 1000].forEach((ms) => setTimeout(() => this._onResize(), ms));
    }

    disconnectedCallback() {
      if (this._track) this._track.removeEventListener('scroll', this._onTrackScroll);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('orientationchange', this._onResize);
      window.removeEventListener('load', this._onResize);
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._io) this._io.disconnect();
    }

    _onResize() {
      this._syncSnapPadding();
      this._updateBar();
      this._syncHint();
    }

    /* RK: staat de voortgangslijn op deze plek uit, dan hoort de tekst er
       ook niet te staan - hij hoort bij die lijn. We kijken naar wat er
       GERENDERD wordt in plaats van de regels na te bouwen die een sectie
       daarvoor uitzendt: die staan achter een eigen sectie-ID en zijn hier
       niet te kennen. Bewust in _onResize en niet in _updateBar: die
       laatste draait bij elke veeg mee, en getComputedStyle is daar te
       duur voor. */
    _syncHint() {
      if (!this._hint || !this._bar) return;
      const barHidden = window.getComputedStyle(this._bar).display === 'none';
      if (this._hint.hidden !== barHidden) this._hint.hidden = barHidden;
    }

    _syncSnapPadding() {
      const track = this._track;
      if (!track) return;
      const styles = window.getComputedStyle(track);
      if (styles.paddingLeft) track.style.scrollPaddingLeft = styles.paddingLeft;
      if (styles.paddingRight) track.style.scrollPaddingRight = styles.paddingRight;
    }

    _scrollable() {
      if (!this._track) return 0;
      return this._track.scrollWidth - this._track.clientWidth;
    }

    _updateBar() {
      const track = this._track;
      if (!track || !this._fill) return;

      const clientWidth = track.clientWidth;
      const scrollWidth = track.scrollWidth;
      if (!clientWidth || !scrollWidth) return;

      const scrollable = scrollWidth - clientWidth;
      const canScroll = scrollable > 1;

      if (canScroll) {
        this.setAttribute('data-rk-scrollable', '');
      } else {
        this.removeAttribute('data-rk-scrollable');
      }

      let ratio = clientWidth / scrollWidth;
      if (!isFinite(ratio) || ratio <= 0) ratio = 1;

      const fillWidthPercent = Math.min(100, Math.max(12, ratio * 100));

      let scrollFraction = canScroll ? track.scrollLeft / scrollable : 0;
      if (!isFinite(scrollFraction)) scrollFraction = 0;
      scrollFraction = Math.min(1, Math.max(0, scrollFraction));

      this._fill.style.width = fillWidthPercent + '%';
      this._fill.style.left = (scrollFraction * (100 - fillWidthPercent)) + '%';
    }

    _step(direction) {
      const track = this._track;
      if (!track) return;

      const first = track.firstElementChild;
      let step = 0;

      if (first) {
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap) || 0;
        step = first.getBoundingClientRect().width + gap;
      }

      if (!isFinite(step) || step <= 0) step = track.clientWidth * 0.9;

      track.scrollBy({ left: step * (direction || 1), behavior: 'smooth' });
    }

    _onBarPointerDown(e) {
      if (this._scrollable() <= 0) return;
      this._dragging = true;
      this._dragStartX = e.clientX;
      this._dragStartScroll = this._track.scrollLeft;
      this._bar.classList.add('rk-swipe--dragging');
      this._bar.setPointerCapture(e.pointerId);
      this._bar.addEventListener('pointermove', this._onBarPointerMove);
      this._bar.addEventListener('pointerup', this._onBarPointerUp);
      this._bar.addEventListener('pointercancel', this._onBarPointerUp);
    }

    _onBarPointerMove(e) {
      if (!this._dragging) return;
      const barRect = this._bar.getBoundingClientRect();
      const deltaX = e.clientX - this._dragStartX;
      const deltaRatio = barRect.width ? deltaX / barRect.width : 0;
      this._track.scrollLeft = this._dragStartScroll + deltaRatio * this._scrollable();
    }

    _onBarPointerUp(e) {
      this._dragging = false;
      this._bar.classList.remove('rk-swipe--dragging');
      try { this._bar.releasePointerCapture(e.pointerId); } catch (err) {}
      this._bar.removeEventListener('pointermove', this._onBarPointerMove);
      this._bar.removeEventListener('pointerup', this._onBarPointerUp);
      this._bar.removeEventListener('pointercancel', this._onBarPointerUp);
    }
  }

  customElements.define('rk-swipe', RkSwipe);
})();
